import { formatPlainDate, formatTime, type PlainDate, type Profile } from "@kedaat/engine";
import type { Locality } from "@kedaat/locations";
import { CONTACT_EMAIL, SITE_NAME } from "./site";
import type { DayResult } from "./zmanim";

/** What the reader saw on screen, so a report can be checked without asking them again. */
export interface ReportContext {
  locality: Locality;
  date: PlainDate;
  profile: Profile;
  day: DayResult;
  /** The zman the report was opened from, when it was opened from one. */
  zman?: string;
  url: string;
}

export interface ReportInput {
  /** What looks wrong, in the reader's words. */
  description: string;
  /** The luach or rav the reader is comparing against. */
  source: string;
}

/** The part of the report the site fills in. Shown to the reader before anything is sent. */
export function reportDetails({ locality, date, profile, day, url }: ReportContext): string {
  const lines = [
    `יישוב: ${locality.nameHe} (${locality.code})`,
    `תאריך: ${formatPlainDate(date)} · ${day.info.hebrewDate}`,
    `שיטה: ${profile.label.he} (${profile.id})`,
  ];
  const overridden = day.rows.filter((row) => row.overridden);
  if (overridden.length > 0) {
    lines.push(
      `התאמות: ${overridden.map((r) => `${r.zman.label.he} → ${r.opinion.id}`).join(", ")}`,
    );
  }
  lines.push("", "הזמנים שהוצגו:");
  for (const row of day.rows) {
    const time =
      row.ms === null ? "—" : formatTime(row.ms, row.opinion.rounding, { seconds: true });
    lines.push(`  ${row.zman.label.he}: ${time} (${row.opinion.id})`);
  }
  lines.push("", url);
  return lines.join("\n");
}

export function reportSubject(context: ReportContext): string {
  const where = `${context.locality.nameHe} ${formatPlainDate(context.date)}`;
  return context.zman
    ? `${SITE_NAME}: אי-התאמה ב${context.zman} · ${where}`
    : `${SITE_NAME}: אי-התאמה בזמנים · ${where}`;
}

export function reportBody(input: ReportInput, context: ReportContext): string {
  return [
    input.description.trim() || "(לא מולא תיאור)",
    "",
    `מול: ${input.source.trim() || "(לא צוין לוח או מקור)"}`,
    "",
    "— פרטי התצוגה —",
    reportDetails(context),
  ].join("\n");
}

/** A mail link the reader's own mail program opens; the site itself sends nothing. */
export function reportMailto(input: ReportInput, context: ReportContext): string {
  const query = new URLSearchParams({
    subject: reportSubject(context),
    body: reportBody(input, context),
  });
  return `mailto:${CONTACT_EMAIL}?${query.toString().replace(/\+/g, "%20")}`;
}
